import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdClose } from 'react-icons/md';
import Modal from '../components/modal';
import Input from '../components/Input';
import { useNavigate } from 'react-router-dom';

const EditProfile = ({ isModalOpen, setIsModalOpen, closeModal, getData }) => {
  // Initialize state with getData (user data passed from parent)
  const [planData, setPlanData] = useState({
    name: getData?.name || '',
    email: getData?.email || '',
    password: getData?.password || '',
    phoneNumber: getData?.phoneNumber || '',
    photo: getData?.photo || null,
    role: 'admin'
  });

  const navigate = useNavigate();

  const [imagePreview, setImagePreview] = useState(getData?.photo || null);
  const [loading, setLoader] = useState(false);

  // Check if the form is valid
  const isFormValid = () => {
    return planData.name && planData.email && planData.phoneNumber;
  };

  // Update state when getData changes
  useEffect(() => {
    if (getData) {
      setPlanData({
        name: getData.name || '',
        email: getData.email || '',
        password: getData?.password || '',
        phoneNumber: getData.phoneNumber || '',
        photo: getData.photo || null,
        role: 'admin'
      });
      setImagePreview(getData.photo || null);
    }
  }, [getData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPlanData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file input changes
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPlanData((prev) => ({
        ...prev,
        photo: file
      }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Trigger file input click
  const handleClick = () => {
    document.getElementById('fileInput').click();
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setPlanData((prev) => ({
      ...prev,
      photo: null
    }));
    setImagePreview(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('Please fill all required fields.');
      return;
    }

    setLoader(true);

    try {
      let photoUrl = planData.photo;

      // Upload new photo if a file is selected
      if (planData.photo instanceof File) {
        const formData = new FormData();
        formData.append('images', planData.photo);

        const uploadResponse = await fetch('https://file-upload-ashen.vercel.app/api/upload', {
          method: 'POST',
          body: formData
        });
        const uploadResult = await uploadResponse.json();

        if (uploadResult?.success === true) {
          photoUrl = uploadResult.data[0];
        } else {
          throw new Error('Failed to upload photo.');
        }
      }

      // Update user profile
      const myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');
      myHeaders.append('Authorization', `Bearer ${localStorage.getItem('token')}`);

      const raw = JSON.stringify({
        name: planData.name,
        email: planData.email,
        phoneNumber: planData.phoneNumber,
        password: planData.password,
        photo: photoUrl
      });

      const updateResponse = await fetch(
        `https://quran-backend-theta.vercel.app/v1/user/update-user/${getData?._id}`,
        {
          method: 'PATCH',
          headers: myHeaders,
          body: raw
        }
      );
      const updateResult = await updateResponse.json();
      console.log(updateResult);

      if (updateResult?.success === true) {
        console.log(updateResult);

        toast.success('Profile updated successfully!');
        closeModal();
        localStorage.setItem('userData', JSON.stringify(updateResult?.data));
        navigate('/dashboard');
      } else {
        toast.error(updateResult?.error);
      }
    } catch (error) {
      console.log(error);

      toast.error(error?.response?.error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="p-3 flex justify-between items-center">
          <span></span>
          <h1 className="capitalize h4 font-medium">Edit Profile</h1>
          <MdClose onClick={() => setIsModalOpen(false)} size={25} className="cursor-pointer" />
        </div>
        <hr />
        <div className="p-5">
          <form onSubmit={handleSubmit}>
            <div className="flex gap-5 flex-wrap">
              {/* Photo Upload Section */}
              <div className="w-full flex flex-col items-center">
                <div onClick={handleClick} className="cursor-pointer">
                  {imagePreview ? (
                    <div className="relative mx-auto rounded-full w-28 h-28">
                      <img
                        src={imagePreview}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
                      >
                        <MdClose size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative mx-auto rounded-full w-28 h-28">
                      <img
                        src={require('../assets/image/profile.jpg')}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full"
                      />
                    </div>
                  )}
                </div>
                <p className="text-primary cursor-pointer font-medium underline mt-2 text-center">
                  Upload New Picture
                </p>
                <input
                  id="fileInput"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </div>

              {/* Input Fields */}
              <div className="w-[48%]">
                <Input
                  label={'Name'}
                  placeholder={'Name'}
                  className={'bg-gray-100 px-2 w-full'}
                  name="name"
                  value={planData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="w-[48%]">
                <Input
                  label={'Email Address'}
                  placeholder={'Email Address'}
                  className={'bg-gray-100 px-2 w-full'}
                  name="email"
                  value={planData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="w-[48%]">
                <Input
                  label={'Phone Number'}
                  placeholder={'Phone Number'}
                  className={'bg-gray-100 px-2 w-full'}
                  name="phoneNumber"
                  value={planData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="w-[48%]">
                <Input
                  label={'Password'}
                  placeholder={'Password'}
                  className={'bg-gray-100 px-2 w-full'}
                  name="password"
                  type="password"
                  value={planData.password}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            {loading ? (
              <button
                disabled
                type="button"
                className="bg-primary w-full text-center mt-3 py-2.5 rounded-lg text-white font-semibold cursor-pointer"
              >
                Loading...
              </button>
            ) : (
              <button
                type="submit"
                className={`w-full mt-3 bg-primary py-2.5 rounded-md text-white text-sm font-medium ${
                  !isFormValid() && 'cursor-not-allowed opacity-50'
                }`}
                disabled={!isFormValid()}
              >
                Save Changes
              </button>
            )}
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default EditProfile;
